import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import type { ReactNode } from "react";
import { isExternalHttpHref, isSafeHref } from "./editorActions";

const safeSchema = {
  tagNames: ["p", "br", "strong", "em", "u", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li", "a", "table", "thead", "tbody", "tr", "th", "td", "blockquote", "code", "pre", "del", "hr"],
  attributes: {
    a: ["href", "title"],
    th: ["align"],
    td: ["align"],
  },
  protocols: { href: ["http", "https", "mailto"] },
};

type MarkdownRendererProps = { value: string; className?: string; "aria-label"?: string };

export function MarkdownRenderer({ value, className = "", "aria-label": ariaLabel }: MarkdownRendererProps) {
  return (
    <div className={`markdown-renderer ${className}`.trim()} role="region" aria-label={ariaLabel}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, safeSchema]]}
        components={{
          a: ({ children, href, title }) => {
            const safeHref = href && isSafeHref(href) ? href : undefined;
            if (!safeHref) return <span>{children}</span>;
            const external = isExternalHttpHref(safeHref);
            return <a href={safeHref} title={title} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer nofollow" : undefined}>{children}</a>;
          },
          // Keep the component map explicit so unsupported HTML cannot become UI.
          img: ({ alt }) => <span>{alt as ReactNode}</span>,
        }}
      >
        {value || ""}
      </ReactMarkdown>
    </div>
  );
}

export { safeSchema };
